import { arrayMove } from '@dnd-kit/sortable';
import { Box, Button, IconButton, Tooltip, useTheme } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { DragIcon, RemoveIconTwoTone, Typography } from '@room-match/shared-ui-components';
import { FormikProps, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { MdClear as Clear } from 'react-icons/md';
import { COLORS } from 'shared/constants/COLORS';
import IMedia from 'shared/interfaces/IMedia';
import { IResumeCredit } from 'shared/interfaces/IResume';
import { ICategory, ICredit } from 'shared/interfaces/ITalent';
import { arraysEqual, GENERATE_DATES } from 'shared/utils/common';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';
import MediaBinModal from '../../../../shared/components/MediaBinModal/MediaBinModal';
import CreditItem from '../CreditItem/CreditItem';
import {
  CustomTextInput,
  ErrorMessageContainer,
  ItemHeader,
  MainCont,
  NewItemField,
  useStyles,
} from './ResumeCategoryItem.styles';
interface Props {
  item: ICategory;
  addCredit: (data: IResumeCredit) => void;
  modifyCredits: (data: ICredit[]) => void;
  hideYears: boolean;
  deleteCategory: () => void;
  ifLastItem: boolean;
  deleting: boolean;
}

const ResumeCategoryItem: React.FC<Props> = ({
  item,
  addCredit,
  modifyCredits,
  hideYears,
  deleteCategory,
  ifLastItem,
  deleting,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [showAdd, setShowAdd] = useState(false);
  const [credits, setCredits] = useState(item.credits);
  const textFieldYear: any = React.useRef(null);
  const textFieldTitle: any = React.useRef(null);
  const textFieldRole: any = React.useRef(null);
  const textFieldDirector: any = React.useRef(null);
  const addButtonRef: any = React.useRef(null);
  const [checkError, setcheckError] = useState(false);
  const [showBin, setShowBin] = useState(false);
  const [showAttachedMedia, setShowAttachedMedia] = useState(false);
  const [selectedMedia, setselectedMedia] = useState<IMedia[]>([]);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    setCredits(item.credits);
  }, [item]);

  const onDragEnd = (result: any) => {
    // dropped outside the list
    if (!result.destination || editMode) {
      return;
    }

    const swappedArray = arrayMove(credits, result.source.index, result.destination.index);

    if (arraysEqual(swappedArray, credits)) return;
    setCredits(swappedArray);
    modifyCredits(swappedArray);
  };

  const getListStyle = (isDraggingOver: boolean) => ({
    padding: 8,
    felx: 1,
    transition: '300ms',
  });

  const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
    display: 'flex',
    userSelect: 'none',
    border: isDragging ? (theme.palette.type === 'dark' ? '2px solid #2d343e' : '2px solid #cdd9e9') : 'none',
    borderTop: isDragging ? 'none ' : 'none',
    backgroundColor: 'transparent',
    ...draggableStyle,
  });

  const initialValues: any = {
    year: '',
    title: '',
    director: '',
    role: '',
    attached_media: [],
  };

  const handleSubmit = async (values: IResumeCredit, helpers: any) => {
    textFieldYear.current.focus();
    const id = uuidv4();
    setCredits([{ ...values, attached_media: selectedMedia, id }, ...credits]);
    addCredit({ ...values, attached_media: selectedMedia, id });
    helpers.resetForm();
    setselectedMedia([]);
    setcheckError(false);
  };

  const createResumeValidationSchema: yup.SchemaOf<any> = yup.object().shape({
    // representation_type: yup.mixed<RepresentationType>().oneOf(Object.values(RepresentationType)).required(),
    year: yup.number().required(),
    title: yup.string().required(),
    director: yup.string().required(),
    role: yup.string().required(),
    attached_media: yup.array(),
  });

  const form: FormikProps<IResumeCredit> = useFormik({
    initialValues,
    validationSchema: createResumeValidationSchema,
    onSubmit: (values, helpers) => {
      handleSubmit(values, helpers);
      return;
    },
  });

  const modifyCredit = (creditID: string, data: ICredit | {}) => {
    if (Object.keys(data).length > 0) {
      const newCredits: any = credits?.map((m) => {
        if (m.id === creditID) {
          return { ...m, ...data };
        }
        return m;
      });

      setCredits(newCredits);
      modifyCredits(newCredits);
    } else {
      const newCredits = credits?.filter((f) => f.id !== creditID);
      setCredits(newCredits);
      modifyCredits(newCredits);
    }
  };

  const btnDisabled = !form.values.year && !form.values.title && !form.values.role && !form.values.director;

  return (
    <MainCont>
      <ItemHeader>
        <DragIcon style={{ height: 18 }} />
        {/* <select ref={dropdown}>
          <option value={6}>6 Results</option>
          <option value={9}>9 Results</option>
          <option value={15}>Max sites</option>
        </select> */}
        <p style={{ flex: 1, marginLeft: 13, fontSize: 16 }}>{item.category}</p>

        {/* <AddButton onClick={() => setShowAdd(true)}>ADD</AddButton> */}
        <IconButton
          disabled={deleting || credits.length > 0 || ifLastItem ? true : false}
          onClick={deleteCategory}
          style={{ filter: deleting || credits.length || ifLastItem ? 'grayscale(100%)' : 'none' }}
        >
          <Tooltip title="Edit Category">
            <RemoveIconTwoTone style={{ height: 16, width: 'auto' }} />
          </Tooltip>
        </IconButton>
      </ItemHeader>
      <NewItemField>
        <Autocomplete
          id="resume-year"
          onChange={(e, value: any) => {
            if (value && value?.key) {
              form.setFieldValue('year', value.key);
            }
          }}
          value={{ key: form.values.year.toString(), value: form.values.year }}
          options={GENERATE_DATES}
          ListboxProps={{ style: { maxHeight: 250 } }}
          noOptionsText="Invalid Year"
          getOptionLabel={(option: any) => option.key}
          classes={{ option: classes.option }}
          renderOption={(option: any) => {
            return <p style={{ fontSize: 16 }}>{option.value}</p>;
          }}
          renderInput={(params: any) => (
            <CustomTextInput
              error={checkError && form.errors.year !== undefined}
              inputRef={textFieldYear}
              placeholder="Year"
              {...params}
              inputProps={{
                ...params.inputProps,
                onKeyPress: (event) => {
                  const { key } = event;
                  if (key === 'Enter') {
                    form.setFieldValue('year', params.inputProps.value);
                    textFieldTitle.current.focus();
                  }
                },
              }}
            />
          )}
        />

        <CustomTextInput
          error={checkError && form.errors.title !== undefined}
          inputRef={textFieldTitle}
          size="small"
          fullWidth
          placeholder="Title"
          InputLabelProps={{ shrink: true }}
          name="title"
          value={form.values.title}
          onChange={form.handleChange}
          inputProps={{
            onKeyPress: (event) => {
              const { key } = event;
              if (key === 'Enter') {
                textFieldRole.current.focus();
              }
            },
          }}
        />
        <CustomTextInput
          error={checkError && form.errors.role !== undefined}
          inputRef={textFieldRole}
          size="small"
          fullWidth
          placeholder="Role"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          name="role"
          value={form.values.role}
          onChange={form.handleChange}
          inputProps={{
            onKeyPress: (event) => {
              const { key } = event;
              if (key === 'Enter') {
                textFieldDirector.current.focus();
              }
            },
          }}
        />
        <CustomTextInput
          error={checkError && form.errors.director !== undefined}
          inputRef={textFieldDirector}
          size="small"
          fullWidth
          placeholder={`${item.category.includes('Print') ? 'Photographer' : 'Director'}`}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          name="director"
          value={form.values.director}
          onChange={form.handleChange}
          inputProps={{
            onKeyPress: (event) => {
              const { key } = event;
              if (key === 'Enter') {
                addButtonRef.current.click();
              }
            },
          }}
        />
        <Button
          style={{
            width: 150,
            height: 36,
            border: '#3e8bff solid 1px ',
            background: 'transparent',
            opacity: btnDisabled ? 0.5 : 1,
          }}
          onClick={() => setShowBin(true)}
          disabled={btnDisabled}
        >
          Add Media
        </Button>
        <Tooltip title="Save">
          <Button
            ref={addButtonRef}
            variant="contained"
            style={{ backgroundColor: COLORS.PRIMARY, color: COLORS.WHITE, height: 36, padding: 0 }}
            onClick={() => {
              setcheckError(true);
              form.handleSubmit();
            }}
          >
            Save
          </Button>
        </Tooltip>
        <Tooltip title="Clear Fields">
          <IconButton
            style={{ padding: 0 }}
            onClick={() => {
              form.resetForm();
              setselectedMedia([]);
            }}
          >
            <Clear style={{ color: '#e34343' }} />
          </IconButton>
        </Tooltip>
        {checkError && Object.keys(form.errors).length > 0 && (
          <ErrorMessageContainer>
            <Typography style={{ color: '#e34343' }} fontSize={14}>
              *All fields are required
            </Typography>
          </ErrorMessageContainer>
        )}
        {selectedMedia.length ? (
          <Box style={{ display: 'flex', alignItems: 'center', marginTop: 10, marginLeft: 10 }}>
            <p style={{ fontSize: 16 }}> {selectedMedia.length} Media Attached : </p>
            <Button
              disabled={selectedMedia.length === 0}
              style={{ marginLeft: 10, border: '#3e8bff solid 1px ', backgroundColor: 'transparent' }}
              onClick={() => setShowAttachedMedia(true)}
            >
              View Media
            </Button>
          </Box>
        ) : (
          <></>
        )}
      </NewItemField>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <div
              className={`credit-item-list disabled-${editMode}`}
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={getListStyle(snapshot.isDraggingOver)}
            >
              {credits?.length ? (
                credits?.map((value: ICredit, index: number) => (
                  <Draggable key={value.id} draggableId={value.id} index={index} isDragDisabled={editMode}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                      >
                        <CreditItem
                          hideYears={hideYears}
                          isPhotographer={item.category.includes('Print')}
                          edit={setEditMode}
                          modifyCredit={(data: ICredit | {}) => modifyCredit(value.id, data)}
                          item={value}
                          key={value.id}
                        />
                      </div>
                    )}
                  </Draggable>
                ))
              ) : (
                <></>
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {showBin && (
        <MediaBinModal
          selected={selectedMedia}
          show={showBin}
          close={() => setShowBin(false)}
          setSelected={setselectedMedia}
        />
      )}
      {showAttachedMedia && (
        <MediaBinModal
          attachedMedia
          selected={selectedMedia}
          show={showAttachedMedia}
          close={() => setShowAttachedMedia(false)}
          setSelected={setselectedMedia}
        />
      )}
    </MainCont>
  );
};

export default ResumeCategoryItem;
